/**
 * Pairs of transactions that look like the same payment written down twice.
 *
 * A pair has to agree on the payment type and on which way the money went,
 * and be close on the rest: the day (within `withinDays`, because a bank
 * re-dates and a hand-typed row guesses), the description (at least
 * `minSimilarity`, because one side is often typed by hand and the other is
 * the bank's shouting version of it), and the amount (at most `amountApart`
 * apart, as a share of the larger — 0, the default, is to the cent; a little
 * more catches a hand-typed row rounded off, or a card payment that settled
 * at a slightly different figure).
 *
 * Pure: no store, no words. The sheet decides what to do with a pair.
 */
import { diffDays } from './dates.js'
import { round2 } from './money.js'

export const DUPLICATE_DEFAULTS = { withinDays: 7, minSimilarity: 0.5, amountApart: 0 }

/**
 * How far apart two amounts are, as a share of the larger: 0 when equal,
 * 0.05 for 95.00 against 100.00. Compared in cents, so float noise never makes
 * two equal amounts "0.0000001 apart".
 */
export function amountsApart(a, b) {
  const x = Math.round(Math.abs(a) * 100)
  const y = Math.round(Math.abs(b) * 100)
  const larger = Math.max(x, y)
  return larger === 0 ? 0 : Math.abs(x - y) / larger
}

/** Lower-cased, stripped of diacritics and punctuation, spaces collapsed. */
const fold = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

function bigrams(text) {
  const out = new Map()
  for (let i = 0; i < text.length - 1; i++) {
    const pair = text.slice(i, i + 2)
    out.set(pair, (out.get(pair) ?? 0) + 1)
  }
  return out
}

/**
 * How alike two descriptions read, 0 to 1: the Sørensen–Dice coefficient over
 * character pairs. Pairs rather than words, so `LIDL BEOGRAD 123` and `Lidl`
 * still share something, and word order does not matter much.
 */
export function similarity(a, b) {
  const x = fold(a)
  const y = fold(b)
  if (x === y) return 1
  if (x.length < 2 || y.length < 2) return 0
  const left = bigrams(x)
  const right = bigrams(y)
  let shared = 0
  for (const [pair, count] of left) shared += Math.min(count, right.get(pair) ?? 0)
  return (2 * shared) / (x.length - 1 + (y.length - 1))
}

/**
 * Every pair that qualifies, closest first (most alike, then nearest in time).
 * A transaction can be in more than one pair — three copies make three pairs —
 * because each is its own question.
 *
 * Rows are bucketed by direction and payment type — and, when amounts must
 * match to the cent, by the amount too — so the amount and the description
 * are only ever compared within a bucket and within the day window.
 *
 * @param {{id: string, date: string, amount: number, description: string,
 *          payment_type?: string}[]} transactions
 * @returns {{a: object, b: object, days: number, similarity: number, amountApart: number}[]}
 *          `a` is the earlier of the two
 */
export function findDuplicates(transactions, options = {}) {
  const { withinDays, minSimilarity, amountApart } = { ...DUPLICATE_DEFAULTS, ...options }
  const exact = !(amountApart > 0)
  const buckets = new Map()
  for (const tx of transactions ?? []) {
    if (!tx?.id || tx.saving) continue
    const way = tx.amount > 0 ? 'in' : 'out'
    const key = `${exact ? round2(tx.amount).toFixed(2) : way}|${fold(tx.payment_type)}`
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(tx)
  }

  const pairs = []
  for (const rows of buckets.values()) {
    if (rows.length < 2) continue
    rows.sort((p, q) => (p.date < q.date ? -1 : p.date > q.date ? 1 : 0))
    for (let i = 0; i < rows.length; i++) {
      for (let j = i + 1; j < rows.length; j++) {
        const days = diffDays(rows[i].date, rows[j].date)
        if (days > withinDays) break
        const apart = amountsApart(rows[i].amount, rows[j].amount)
        // A hair's allowance for the division; the figures are in cents.
        if (apart > amountApart + 1e-9) continue
        const score = similarity(rows[i].description, rows[j].description)
        if (score >= minSimilarity) pairs.push({ a: rows[i], b: rows[j], days, similarity: score, amountApart: apart })
      }
    }
  }
  // Closest first: the most alike, then the nearest in amount, then in time.
  return pairs.sort(
    (p, q) =>
      q.similarity - p.similarity || p.amountApart - q.amountApart || p.days - q.days || (p.a.date < q.a.date ? 1 : -1),
  )
}
